import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT, useI18n } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Check, CheckCircle2, Clock } from "lucide-react";

export default function CollabPackageBooked() {
  const { bookingId } = useParams();
  const t = useT();
  const { locale } = useI18n();
  usePageMeta(t("cov.booked_title"));
  const [booking, setBooking] = useState(undefined);

  useEffect(() => {
    if (!bookingId) return;
    api.get(`/collab-offerings/bookings/${bookingId}`).then((r) => setBooking(r.data)).catch(() => setBooking(null));
  }, [bookingId]);

  const selectionByBlock = useMemo(() => {
    const m = {};
    for (const s of (booking?.one_on_one_selections || [])) m[s.block_id] = s;
    return m;
  }, [booking]);

  if (booking === undefined) return <Spinner label={t("common.loading")} />;
  const offering = booking?.offering || {};
  const coachNames = (offering.coaches || []).map((c) => c.name).join(" · ");

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="collab-package-booked">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary">
          <Check className="h-8 w-8 text-primary-foreground" />
        </div>
        <h1 className="font-display text-3xl text-foreground">{t("cov.booked_title")}</h1>
        <p className="max-w-md text-sm text-muted-foreground">{t("cov.booked_sub")}</p>
      </div>

      {offering.title ? (
        <div className="rounded-2xl bg-accent/40 p-5 text-center">
          <p className="font-display text-xl text-foreground">{offering.title}</p>
          {coachNames ? <p className="mt-1 text-sm text-muted-foreground">{coachNames}</p> : null}
          <p className="mt-2 font-display text-2xl font-semibold text-primary">{eur(offering.total_price_cents || 0)}</p>
        </div>
      ) : null}

      {(offering.blocks || []).length > 0 ? (
        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">{t("cov.booked_blocks")}</p>
          <div className="space-y-2">
            {offering.blocks.map((b, i) => {
              const sel = selectionByBlock[b.id];
              let line = t("cov.booked_ready");
              let pending = false;
              if (b.kind === "one_on_one") {
                if (sel?.slot_datetime) line = t("cov.booked_slot", { when: new Date(sel.slot_datetime).toLocaleString(locale, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) });
                else { line = t("cov.booked_slot_pending"); pending = true; }
              }
              return (
                <div key={b.id || i} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
                  <span className="rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-primary">{t(`cov.kind_${b.kind}`)}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{b.title}</p>
                    <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
                      {pending ? <Clock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3 text-emerald-500" />} {line}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <Button className="w-full rounded-full" size="lg" asChild data-testid="booked-primary-home">
        <Link to="/app/bookings">{t("cov.booked_home")}</Link>
      </Button>
    </div>
  );
}
